import React from "react";
import { RotaButton } from "@teamrota/rota-design";
import moment from "moment";
import find from "lodash/fp/find";
import getOr from "lodash/fp/getOr";
import ReactTags from "react-tag-autocomplete";
import ActionDropdown from "~/src/components/action-dropdown";
import FiltersDateRange from "~/src/components/form-components/filters-date-range";
import { BOOKING_STATES_LABELS } from "~/src/consts";
import { removeAtIndex } from "~/src/utils";
import { ButtonContainer } from "./filters.styles";
import getTagsByAccount from "./get-tags-by-account";

const OPTIONS_BOOLSTATE = [
  { label: "Yes", value: true },
  { label: "No", value: false }
];

const OPTIONS_TRISTATE = [
  { label: "Both", value: null },
  { label: "Yes", value: true },
  { label: "No", value: false }
];

const BOOKING_STATE_OPTIONS = Object.entries(BOOKING_STATES_LABELS).map(
  ([id, name]) => ({
    id,
    name
  })
);

const findOption = value =>
  find(option => option.value === value, OPTIONS_TRISTATE);

const readDate = date => (date ? moment(date, "DD/MM/YYYY") : "");

const formatDate = date => (date ? moment(date).format("DD/MM/YYYY") : "");

const Filters = ({
  filters,
  roleOptions,
  serviceAreaOptions,
  onUpdate,
  isRotaAccount,
  onResetState,
  tagsByAccount = []
}) => (
  <div>
    <FiltersDateRange
      startTime={readDate(filters?.startTime)}
      endTime={readDate(filters?.endTime)}
      onChangeStartTime={startTime =>
        onUpdate({ startTime: formatDate(startTime) })
      }
      onChangeEndTime={endTime => onUpdate({ endTime: formatDate(endTime) })}
    />

    <p>Booking states</p>
    <ReactTags
      autofocus={false}
      placeholder="Any"
      handleDelete={index => {
        const bookingStateIn = getOr([], "bookingStateIn", filters);
        onUpdate({
          bookingStateIn: removeAtIndex(bookingStateIn, index)
        });
      }}
      handleAddition={bookingState => {
        onUpdate({
          bookingStateIn: [
            ...getOr([], "bookingStateIn", filters),
            bookingState
          ]
        });
      }}
      tags={getOr([], "bookingStateIn", filters)}
      suggestions={BOOKING_STATE_OPTIONS}
    />

    <p>Finalised</p>
    <ActionDropdown
      isFullWidth
      isBlock
      isOutline
      isSquare
      value={findOption(filters.isFinalised)}
      onChange={option => onUpdate({ isFinalised: option.value })}
      options={OPTIONS_TRISTATE}
    />

    <p>Cancelled</p>
    <ActionDropdown
      isFullWidth
      isBlock
      isOutline
      isSquare
      value={findOption(filters.isCancelled)}
      onChange={option => onUpdate({ isCancelled: option.value })}
      options={OPTIONS_TRISTATE}
    />

    <p>Under review</p>
    <ActionDropdown
      isFullWidth
      isBlock
      isOutline
      isSquare
      value={findOption(filters.isUnderReview)}
      onChange={option => onUpdate({ isUnderReview: option.value })}
      options={OPTIONS_TRISTATE}
    />

    <p>Restricted</p>
    <ActionDropdown
      isFullWidth
      isBlock
      isOutline
      isSquare
      value={findOption(filters.isRestricted)}
      onChange={option => onUpdate({ isRestricted: option.value })}
      options={OPTIONS_TRISTATE}
    />

    <p>Rebook</p>
    <ActionDropdown
      isFullWidth
      isBlock
      isOutline
      isSquare
      value={findOption(filters.isShiftRebook)}
      onChange={option => onUpdate({ isShiftRebook: option.value })}
      options={OPTIONS_BOOLSTATE}
    />

    <p>Featured</p>
    <ActionDropdown
      isFullWidth
      isBlock
      isOutline
      isSquare
      value={findOption(filters.isFeatured)}
      onChange={option => onUpdate({ isFeatured: option.value })}
      options={OPTIONS_TRISTATE}
    />

    <p>Roles</p>
    <ReactTags
      autofocus={false}
      placeholder="Any"
      handleDelete={index => {
        const roleIds = getOr([], "roleIds", filters);
        onUpdate({
          roleIds: removeAtIndex(roleIds, index)
        });
      }}
      handleAddition={role => {
        onUpdate({
          roleIds: [...getOr([], "roleIds", filters), role]
        });
      }}
      tags={filters.roleIds}
      suggestions={roleOptions}
    />

    <p>Tags</p>
    <ReactTags
      autofocus={false}
      placeholder="Any"
      handleDelete={index => {
        const tags = getOr([], "tags", filters);
        onUpdate({
          tags: removeAtIndex(tags, index)
        });
      }}
      handleAddition={role => {
        onUpdate({
          tags: [...getOr([], "tags", filters), role]
        });
      }}
      tags={filters.tags}
      suggestions={tagsByAccount}
    />

    <p>Service Areas</p>
    <ReactTags
      autofocus={false}
      placeholder="Any"
      handleDelete={index => {
        const serviceAreaIds = getOr([], "serviceAreaIds", filters);
        onUpdate({
          serviceAreaIds: removeAtIndex(serviceAreaIds, index)
        });
      }}
      handleAddition={serviceArea => {
        onUpdate({
          serviceAreaIds: [...getOr([], "serviceAreaIds", filters), serviceArea]
        });
      }}
      tags={filters.serviceAreaIds}
      suggestions={serviceAreaOptions}
    />

    <p>Partner managed</p>
    <ActionDropdown
      isFullWidth
      isBlock
      isOutline
      isSquare
      value={findOption(filters.isPartnerManaged)}
      onChange={isPartnerManaged =>
        onUpdate({ isPartnerManaged: isPartnerManaged.value })
      }
      options={OPTIONS_TRISTATE}
    />

    {isRotaAccount && (
      <div>
        <p>Registration/Waiting List</p>
        <ActionDropdown
          isFullWidth
          isBlock
          isOutline
          isSquare
          value={findOption(filters.isRegistration)}
          onChange={option => onUpdate({ isRegistration: option.value })}
          options={OPTIONS_TRISTATE}
        />
      </div>
    )}

    <ButtonContainer>
      <RotaButton variant="outlined" onClick={() => onResetState()}>
        Reset filters
      </RotaButton>
    </ButtonContainer>
  </div>
);

export default getTagsByAccount(Filters);
